import {MenuConfig, MenuItemConfig} from '@common/menus/menu-config';
import {canOpenStaffPage} from '@common/auth/staff-page-access';
import {useSettings} from '@ui/settings/use-settings';
import {User} from '@ui/types/user';
import dot from 'dot-object';
import {useMemo} from 'react';
import {useAuth} from '../auth/use-auth';

export function useCustomMenu(menuOrPosition?: string | MenuConfig) {
  const settings = useSettings();
  const {user, hasPermission} = useAuth();

  return useMemo(() => {
    if (!menuOrPosition) {
      return null;
    }

    const menu =
      typeof menuOrPosition === 'string'
        ? settings.menus?.find(s => s.positions?.includes(menuOrPosition))
        : menuOrPosition;

    // Recursive filter — applies the role/permission/setting/subscription
    // gates to a list of items, and to each item's children. Group items
    // are kept even though they have no `action`, but are dropped when
    // they end up with zero visible children (no point showing an empty
    // folder).
    const filter = (items: MenuItemConfig[]): MenuItemConfig[] => {
      const out: MenuItemConfig[] = [];
      for (const item of items) {
        const hasRoles = (item.roles || []).every(a =>
          user?.roles?.find(b => b.id === a),
        );
        const hasPermissions = (item.permissions || []).every(a =>
          hasPermission(a),
        );
        const hasSettings =
          !item.settings ||
          Object.entries(item.settings).every(([key, value]) => {
            return dot.pick(key, settings) == value;
          });

        if (
          !hasRoles ||
          !hasPermissions ||
          !hasSettings ||
          !subscriptionStatusMatches(item, user)
        ) {
          continue;
        }

        if (item.type === 'group') {
          const children = filter(item.children || []);
          if (children.length === 0) continue; // hide empty groups
          out.push({...item, children});
          continue;
        }

        // Section dividers have no action and aren't navigable but should
        // still render. The admin sidebar's customMenuRender handles them.
        if ((item as any).type === 'section') {
          out.push(item);
          continue;
        }

        // Non-group items must have an action to be navigable.
        if (!item.action) continue;

        // Items with no explicit permission take the rule of the page they
        // link to (config/staff-access.php), so a new menu item pointing at a
        // restricted page is hidden automatically.
        if (!item.permissions?.length && !canOpenStaffPage(item.action, hasPermission)) {
          continue;
        }

        if (user?.id) {
          item.action = item.action.replace(/{currentUser}/g, `${user.id}`);
        }
        out.push(item);
      }
      return out;
    };

    if (!menu) return null;

    // Return a NEW menu object — mutating the settings-backed one causes
    // subscribers to re-render in an infinite loop because the filter
    // produces fresh group objects via {...item, children} each cycle.
    return {
      ...menu,
      items: filter(menu.items),
    };
  }, [hasPermission, settings, menuOrPosition, user]);
}

function subscriptionStatusMatches(
  item: MenuItemConfig,
  user?: User | null,
): boolean {
  if (!item.subscriptionStatus) {
    return true;
  }
  const hasActiveSubscription = !!user?.subscriptions?.find(sub => sub.active);
  if (item.subscriptionStatus === 'subscribed') {
    return hasActiveSubscription;
  }
  return !hasActiveSubscription;
}
